import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Pipeline } from 'app/modules/projects/projects.types';
import { Account, PipelineTemplate } from 'app/core/models/account.types';
import { Invitation } from 'app/core/models';
import { SKIP_FORBIDDEN_REDIRECT } from '@core/interceptors/http-context.tokens';

@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  // Private
  private _account: BehaviorSubject<Account | null>;
  private _accounts: BehaviorSubject<Account[] | null>;

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(private _httpClient: HttpClient) {
    // Set the private defaults
    this._account = new BehaviorSubject(null);
    this._accounts = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for contact
   */
  get account$(): Observable<Account> {
    return this._account.asObservable();
  }

  /**
   * Getter for contacts
   */
  get accounts$(): Observable<Account[]> {
    return this._accounts.asObservable();
  }

  get accountsValue(): Account[] {
    return this._accounts.value;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get contacts
   *
   * @param sortField
   * @param sortDirection
   */
  getAccounts(
    sortField: string = 'name',
    sortDirection: 'asc' | 'desc' | '' = 'asc',
    options?: { skipForbiddenRedirect?: boolean },
  ): Observable<Account[]> {
    const context = options?.skipForbiddenRedirect
      ? new HttpContext().set(SKIP_FORBIDDEN_REDIRECT, true)
      : undefined;

    return this._httpClient
      .get<{ accounts: Account[] }>('/accounts', {
        params: {
          sortField,
          sortDirection,
        },
        ...(context ? { context } : {}),
      })
      .pipe(
        tap(({ accounts }) => {
          this._accounts.next(accounts);
        }),
        map(({ accounts }) => accounts),
      );
  }

  setAccounts(accounts: Account[]): void {
    this._accounts.next(accounts);
  }

  updateAccount(account: Partial<Account>): Observable<Account> {
    return this.accounts$.pipe(
      take(1),
      switchMap((accounts) =>
        this._httpClient.put<any>(`accounts/${account.id}`, { account }).pipe(
          map((response) => {
            const nextAccounts = accounts?.map((existingAccount) =>
              existingAccount.id === response.account.id
                ? { ...existingAccount, ...response.account }
                : existingAccount,
            ) ?? [response.account];

            this._accounts.next(nextAccounts);

            return response.account;
          }),
        ),
      ),
    );
  }

  /**
   * Create account
   */
  createAccount(account: Account): Observable<Account> {
    return this.accounts$.pipe(
      take(1),
      switchMap((accounts) =>
        this._httpClient.post<any>(`accounts`, { account: account }).pipe(
          map((response) => {
            // Update the accounts with the new account
            this._accounts.next([response.account, ...accounts]);

            // Return the new contact
            return response.account;
          }),
        ),
      ),
    );
  }

  getPipelineTemplates(): Observable<PipelineTemplate[]> {
    return this._httpClient.get<any>(`/pipeline_templates`).pipe(
      map((response) => {
        return response.templates;
      }),
    );
  }

  createPipeline(accountId: number, pipeline: Pipeline): Observable<Pipeline> {
    return this._httpClient.post<any>(`pipelines`, { pipeline: pipeline }).pipe(
      map((reponse) => {
        return reponse.pipeline;
      }),
    );
  }

  sendInvitation(accountId: number, invitation: Invitation): Observable<Invitation> {
    return this._httpClient.post<any>(`accounts/${accountId}/invitations`, { invitation: invitation }).pipe(
      map((reponse) => {
        // Return the new contact
        return reponse.invitation;
      }),
    );
  }
}
